import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import {
  shippingReportService,
  CorreiosIntegration,
} from '../services/shippingReportService';
import { correiosComplaintUrl } from '../utils/correiosTicket';

interface CorreiosTicketModalProps {
  isOpen: boolean;
  /** Objetos de rastreio do pedido — um chamado por objeto (volume). */
  objectCodes: string[];
  /** Identificador exibido no título (nº do pedido). */
  orderLabel?: string | number;
  /** Nome do destinatário, pedido no formulário dos Correios. */
  recipientName?: string | null;
  onClose: () => void;
}

/** Campo somente-leitura com botão de copiar — o formulário dos Correios é digitado à mão. */
const CopyField: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono = true,
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard bloqueado (contexto inseguro/permissão): o valor segue visível para digitar.
      setCopied(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="flex items-center gap-2">
        <span className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{value}</span>
        <button
          type="button"
          onClick={copy}
          title={`Copiar ${label.toLowerCase()}`}
          className="text-slate-400 hover:text-emerald-600"
        >
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
};

/**
 * Orientação para abrir um chamado (reclamação de objeto) no Fale Conosco dos Correios.
 *
 * O formulário dos Correios exige login no Meu Correios e o preenchimento do contrato e do
 * cartão de postagem, que ficam em [Shipping].[CorreiosIntegration] — buscados aqui para que
 * o atendimento não precise procurá-los. Cada volume tem seu próprio código de objeto e, por
 * consequência, sua própria reclamação.
 */
export const CorreiosTicketModal: React.FC<CorreiosTicketModalProps> = ({
  isOpen,
  objectCodes,
  orderLabel,
  recipientName,
  onClose,
}) => {
  const [integration, setIntegration] = useState<CorreiosIntegration | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setIntegration(await shippingReportService.getCorreiosIntegration());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar os dados da integração dos Correios');
      setIntegration(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const footer = (
    <div className="flex justify-end">
      <Button variant="outline" onClick={onClose}>
        Fechar
      </Button>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={orderLabel ? `Abrir chamado nos Correios — pedido ${orderLabel}` : 'Abrir chamado nos Correios'}
      footer={footer}
      size="lg"
    >
      <div className="space-y-5">
        {/* Objetos: um chamado por volume */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">
            {objectCodes.length > 1 ? 'Objetos do pedido (um chamado por volume)' : 'Objeto do pedido'}
          </h4>
          <div className="space-y-2">
            {objectCodes.map((code) => (
              <div
                key={code}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <CopyField label="Código do objeto" value={code} />
                <a
                  href={correiosComplaintUrl(code)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline"
                >
                  Abrir formulário dos Correios <ExternalLink size={13} />
                </a>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            O link já leva o código do objeto preenchido. Os Correios pedem login no Meu Correios
            antes de exibir o formulário — use a conta corporativa vinculada ao contrato.
          </p>
        </div>

        {/* Dados da integração usados no formulário */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Dados da postagem</h4>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="animate-spin" size={15} /> Carregando contrato e cartão de postagem…
            </div>
          ) : error ? (
            <div className="text-sm text-red-600">
              {error}{' '}
              <button type="button" onClick={load} className="font-medium underline hover:text-red-700">
                Tentar novamente
              </button>
            </div>
          ) : integration ? (
            <div className="rounded-lg border border-slate-200 px-3 py-2 space-y-2">
              <div className="flex flex-wrap gap-6">
                <CopyField label="Contrato" value={integration.contrato} />
                <CopyField label="Cartão de postagem" value={integration.cartao_postagem} />
                {recipientName && <CopyField label="Destinatário" value={recipientName} mono={false} />}
              </div>
              {integration.ambiente !== 'producao' && (
                <p className="flex items-center gap-1.5 text-xs text-amber-700">
                  <AlertTriangle size={13} />
                  A integração está apontada para <strong>{integration.ambiente}</strong> — confirme o
                  contrato antes de abrir o chamado.
                </p>
              )}
            </div>
          ) : null}
        </div>

        {/* Passo a passo */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Como preencher</h4>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600">
            <li>Abra o link do objeto e faça login no <strong>Meu Correios</strong> com a conta da empresa.</li>
            <li>Confirme o <strong>código do objeto</strong> já preenchido pelo link.</li>
            <li>
              Escolha o <strong>tipo de manifestação</strong>: "objeto não entregue" quando o prazo de
              entrega já venceu e o rastreio não mostra entrega nem devolução; "avariado" ou "violado"
              quando o conteúdo chegou danificado ou faltando.
            </li>
            <li>Informe o <strong>contrato</strong> e o <strong>cartão de postagem</strong> acima.</li>
            <li>
              Preencha os dados do solicitante (a Viver Organizer é a <strong>remetente</strong>): nome/CNPJ,
              endereço, telefone e e-mail de contato — é para esse e-mail que vem o protocolo.
            </li>
            <li>
              Anexe os comprovantes (anexo é obrigatório, até 10 MB): a <strong>nota fiscal</strong> da
              encomenda e, em avaria/violação, fotos do volume e do conteúdo.
            </li>
            <li>
              Na mensagem, descreva o caso com o nº do pedido, a data de postagem e o prazo de entrega
              estourado.
            </li>
            <li>Envie e <strong>guarde o número do protocolo</strong>. A resposta sai em até 5 dias úteis.</li>
          </ol>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            Prazo para reclamar: até <strong>90 dias corridos</strong> da postagem para objeto não
            entregue e <strong>30 dias</strong> da entrega para avaria ou violação.
          </p>
        </div>
      </div>
    </Dialog>
  );
};

export default CorreiosTicketModal;
